import { ATTENDANCE, ATTENDANCE_LABELS, type AttendanceRow, type AttendanceStatus } from '@hoopify/shared';

/** One row per player with Here / Late / Sitting out / Absent buttons. `big` is for courtside. */
export function AttendanceList({ rows, onSet, readOnly, big }: { rows: AttendanceRow[]; onSet: (playerId: string, status: AttendanceStatus | null) => void; readOnly?: boolean; big?: boolean }) {
  return (
    <ul className={`divide-y ${big ? 'divide-white/10' : 'divide-line rounded-lg border border-line bg-surface'}`}>
      {rows.map((r) => (
        <li key={r.player.id} className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${big ? 'py-3' : 'px-4 py-2.5'}`}>
          <div className="min-w-0 flex-1 basis-[160px]">
            <p className={`truncate font-medium ${big ? 'text-[18px]' : ''}`}>
              {r.player.number && <span className="tnum mr-1.5 text-muted">{r.player.number}</span>}
              {r.player.name}
            </p>
            {r.unavailable && (
              <p className={`truncate ${big ? 'text-[14px] text-warn' : 'text-[13px] text-warn'}`}>
                Can’t make it{r.unavailable.reason ? `: ${r.unavailable.reason}` : ''} ({r.unavailable.by})
              </p>
            )}
          </div>
          {readOnly ? (
            <span className="text-[14px] text-muted">{r.status ? ATTENDANCE_LABELS[r.status] : 'Not marked'}</span>
          ) : (
            <div className={`grid grid-cols-4 gap-1 ${big ? 'w-full sm:w-auto' : ''}`} role="radiogroup" aria-label={`Attendance for ${r.player.name}`}>
              {ATTENDANCE.map((s) => {
                const on = r.status === s;
                // Filled with the theme's paper colour as text: ink on bright fills in dark mode, chalk on deep fills in light mode.
                const tone = s === 'absent' ? 'bg-danger text-paper' : s === 'present' ? 'bg-ok text-paper' : s === 'late' ? 'bg-warn text-paper' : 'bg-blue text-brand-ink';
                return (
                  <button
                    key={s}
                    role="radio"
                    aria-checked={on}
                    onClick={() => onSet(r.player.id, on ? null : s)}
                    className={`rounded-md font-medium whitespace-nowrap ${big ? 'px-2 py-3 text-[15px]' : 'px-2 py-1 text-[13px]'} ${
                      on ? tone : big ? 'bg-white/10 text-white/80' : 'bg-sunken text-muted hover:text-ink'
                    }`}
                  >
                    {ATTENDANCE_LABELS[s]}
                  </button>
                );
              })}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function attendanceSummary(rows: AttendanceRow[]) {
  const count = (s: AttendanceStatus) => rows.filter((r) => r.status === s).length;
  const parts = [`${count('present')} here`];
  if (count('late')) parts.push(`${count('late')} late`);
  if (count('sitting_out')) parts.push(`${count('sitting_out')} sitting out`);
  if (count('absent')) parts.push(`${count('absent')} absent`);
  const unmarked = rows.filter((r) => !r.status).length;
  if (unmarked) parts.push(`${unmarked} not marked`);
  return parts.join(', ');
}
